import { create } from 'zustand';
import { supabase } from '../supabase';
import { friendlyDbError } from '../utils/friendlyDbError';
import type { LeadFormDocumentRequirement } from '../types/leadForm';

/**
 * Raccolte documenti di un cliente (o di un progetto, o di un'offerta).
 *
 * Una raccolta e' l'elenco di cio' che una persona deve consegnare, piu' il
 * collegamento con cui lo consegna. I file che arrivano diventano documenti
 * normali del CRM: qui teniamo solo il legame fra il documento e la voce che
 * soddisfa, cosi' si vede a colpo d'occhio cosa manca ancora.
 */

export interface DocumentoDiRaccolta {
  id: string;
  name: string;
  url: string;
  requirement_id: string | null;
  verified_at: string | null;
  created_at: string | null;
}

export interface RaccoltaDocumenti {
  id: string;
  organization_id: string;
  client_id: string | null;
  project_id: string | null;
  deal_id: string | null;
  form_id: string | null;
  field_id: string | null;
  requirements: LeadFormDocumentRequirement[] | null;
  token: string;
  expires_at: string | null;
  completed_at: string | null;
  last_reminder_at: string | null;
  created_at: string;
  /** Voci effettive: dal modulo quando c'e', altrimenti quelle della riga. */
  voci: LeadFormDocumentRequirement[];
  documenti: DocumentoDiRaccolta[];
  /** Solo nella vista d'insieme: a chi appartiene e da quale modulo arriva. */
  nomeCliente?: string | null;
  nomeModulo?: string;
}

interface Aggancio {
  client_id?: string;
  project_id?: string;
  deal_id?: string;
}

interface Stato {
  raccolte: RaccoltaDocumenti[];
  caricamento: boolean;
  errore: string | null;
  carica: (aggancio: Aggancio) => Promise<void>;
  /** Tutte le raccolte dell'organizzazione, per la vista d'insieme. */
  caricaTutte: (soloAperte: boolean) => Promise<void>;
  crea: (
    aggancio: Aggancio,
    voci: LeadFormDocumentRequirement[],
    giorniValidita: number,
  ) => Promise<RaccoltaDocumenti | null>;
  segnaVerificato: (documentoId: string, verificato: boolean) => Promise<void>;
  rinnova: (raccoltaId: string, giorni: number) => Promise<void>;
  elimina: (raccoltaId: string) => Promise<void>;
}

/** Gettone url-safe da 32 caratteri: chi lo indovinasse vedrebbe documenti altrui. */
export function generaGettone(): string {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/** L'indirizzo da mandare a chi deve consegnare. */
export function linkRaccolta(token: string): string {
  return `${window.location.origin}/documenti/${token}`;
}

const SELECT_RACCOLTA =
  'id, organization_id, client_id, project_id, deal_id, form_id, field_id, requirements, token, expires_at, completed_at, last_reminder_at, created_at';

export const useDocumentCollectionStore = create<Stato>((set, get) => ({
  raccolte: [],
  caricamento: false,
  errore: null,

  carica: async (aggancio) => {
    const chiave = Object.keys(aggancio)[0] as keyof Aggancio | undefined;
    if (!chiave || !aggancio[chiave]) return;

    set({ caricamento: true, errore: null });
    try {
      const { data: righe, error } = await supabase
        .from('document_collections')
        .select(SELECT_RACCOLTA)
        .eq(chiave, aggancio[chiave] as string)
        .order('created_at', { ascending: false });
      if (error) throw error;

      const ids = (righe ?? []).map((r) => r.id);
      const { data: documenti } = ids.length
        ? await supabase
            .from('documents')
            .select('id, name, url, requirement_id, verified_at, created_at, collection_id')
            .in('collection_id', ids)
        : { data: [] as any[] };

      // Le voci di una raccolta nata da un modulo vivono nel costruttore.
      const formIds = [...new Set((righe ?? []).map((r) => r.form_id).filter(Boolean))] as string[];
      const { data: moduli } = formIds.length
        ? await supabase.from('lead_forms').select('id, fields').in('id', formIds)
        : { data: [] as any[] };

      const raccolte: RaccoltaDocumenti[] = (righe ?? []).map((r) => {
        let voci = (r.requirements ?? []) as LeadFormDocumentRequirement[];
        if (r.form_id && r.field_id) {
          const modulo = (moduli ?? []).find((m: any) => m.id === r.form_id);
          const campo = ((modulo?.fields ?? []) as any[]).find((c) => c.id === r.field_id);
          if (campo?.documentsConfig?.requirements) voci = campo.documentsConfig.requirements;
        }
        return {
          ...(r as any),
          voci,
          documenti: (documenti ?? []).filter((d: any) => d.collection_id === r.id),
        };
      });

      set({ raccolte, caricamento: false });

      // Lo stato "completata" salvato puo' essere rimasto indietro: se qualcuno
      // cancella un documento dalla lista dei documenti, la voce torna scoperta
      // ma la riga non lo sa. Qui la riallineiamo, cosi' le automazioni che
      // guardano quel campo non solleciteranno chi ha gia' consegnato (e
      // viceversa).
      for (const raccolta of raccolte) {
        const obbligatorie = raccolta.voci.filter((v) => v.required);
        const completaDavvero =
          obbligatorie.length > 0 &&
          obbligatorie.every((v) => raccolta.documenti.some((d) => d.requirement_id === v.id));
        if (completaDavvero === !!raccolta.completed_at) continue;
        const completed_at = completaDavvero ? new Date().toISOString() : null;
        void supabase
          .from('document_collections')
          .update({ completed_at })
          .eq('id', raccolta.id)
          .then(() =>
            set({
              raccolte: get().raccolte.map((r) =>
                r.id === raccolta.id ? { ...r, completed_at } : r,
              ),
            }),
          );
      }
    } catch (error) {
      set({ errore: friendlyDbError(error), caricamento: false });
    }
  },

  caricaTutte: async (soloAperte) => {
    set({ caricamento: true, errore: null });
    try {
      let query = supabase
        .from('document_collections')
        .select(`${SELECT_RACCOLTA}, clients(business_name, contact_name)`)
        .order('created_at', { ascending: false })
        .limit(300);
      if (soloAperte) query = query.is('completed_at', null);

      const { data: righe, error } = await query;
      if (error) throw error;

      const ids = (righe ?? []).map((r: any) => r.id);
      const { data: documenti } = ids.length
        ? await supabase
            .from('documents')
            .select('id, name, url, requirement_id, verified_at, created_at, collection_id')
            .in('collection_id', ids)
        : { data: [] as any[] };

      const formIds = [...new Set((righe ?? []).map((r: any) => r.form_id).filter(Boolean))] as string[];
      const { data: moduli } = formIds.length
        ? await supabase.from('lead_forms').select('id, name, fields').in('id', formIds)
        : { data: [] as any[] };

      const raccolte: RaccoltaDocumenti[] = (righe ?? []).map((r: any) => {
        let voci = (r.requirements ?? []) as LeadFormDocumentRequirement[];
        let nomeModulo: string | undefined;
        if (r.form_id && r.field_id) {
          const modulo = (moduli ?? []).find((m: any) => m.id === r.form_id);
          nomeModulo = modulo?.name;
          const campo = ((modulo?.fields ?? []) as any[]).find((c) => c.id === r.field_id);
          if (campo?.documentsConfig?.requirements) voci = campo.documentsConfig.requirements;
        }
        return {
          ...r,
          voci,
          nomeCliente: r.clients?.business_name || r.clients?.contact_name || null,
          nomeModulo,
          documenti: (documenti ?? []).filter((d: any) => d.collection_id === r.id),
        };
      });

      set({ raccolte, caricamento: false });
    } catch (error) {
      set({ errore: friendlyDbError(error), caricamento: false });
    }
  },

  crea: async (aggancio, voci, giorniValidita) => {
    try {
      const { data: utente } = await supabase.auth.getUser();
      const { data: profilo } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', utente.user?.id ?? '')
        .single();
      if (!profilo?.organization_id) throw new Error('Utente senza organizzazione');

      const token = generaGettone();
      const { data, error } = await supabase
        .from('document_collections')
        .insert({
          organization_id: profilo.organization_id,
          client_id: aggancio.client_id ?? null,
          project_id: aggancio.project_id ?? null,
          deal_id: aggancio.deal_id ?? null,
          requirements: voci,
          token,
          expires_at:
            giorniValidita > 0
              ? new Date(Date.now() + giorniValidita * 86400000).toISOString()
              : null,
          created_by: utente.user?.id ?? null,
        })
        .select(SELECT_RACCOLTA)
        .single();
      if (error) throw error;

      const nuova: RaccoltaDocumenti = { ...(data as any), voci, documenti: [] };
      set({ raccolte: [nuova, ...get().raccolte] });
      return nuova;
    } catch (error) {
      set({ errore: friendlyDbError(error) });
      return null;
    }
  },

  segnaVerificato: async (documentoId, verificato) => {
    const { data: utente } = await supabase.auth.getUser();
    const quando = verificato ? new Date().toISOString() : null;
    const { error } = await supabase
      .from('documents')
      .update({ verified_at: quando, verified_by: verificato ? utente.user?.id ?? null : null })
      .eq('id', documentoId);
    if (error) {
      set({ errore: friendlyDbError(error) });
      return;
    }
    set({
      raccolte: get().raccolte.map((r) => ({
        ...r,
        documenti: r.documenti.map((d) =>
          d.id === documentoId ? { ...d, verified_at: quando } : d,
        ),
      })),
    });
  },

  rinnova: async (raccoltaId, giorni) => {
    const scadenza = giorni > 0 ? new Date(Date.now() + giorni * 86400000).toISOString() : null;
    const { error } = await supabase
      .from('document_collections')
      .update({ expires_at: scadenza })
      .eq('id', raccoltaId);
    if (error) {
      set({ errore: friendlyDbError(error) });
      return;
    }
    set({
      raccolte: get().raccolte.map((r) =>
        r.id === raccoltaId ? { ...r, expires_at: scadenza } : r,
      ),
    });
  },

  elimina: async (raccoltaId) => {
    const { error } = await supabase.from('document_collections').delete().eq('id', raccoltaId);
    if (error) {
      set({ errore: friendlyDbError(error) });
      return;
    }
    set({ raccolte: get().raccolte.filter((r) => r.id !== raccoltaId) });
  },
}));
